import { createContext, useContext, useEffect, useRef, useState } from "react";
import api from "../Component/utils/api";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [token, setToken] = useState(
    () => (typeof window !== "undefined" ? (localStorage.getItem("token") || null) : null)
  );
  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(
    () => typeof window !== "undefined" ? (!!localStorage.getItem("token") || !!localStorage.getItem("rememberToken")) : false
  );
  const [authError, setAuthError] = useState(null);
  const hasMounted = useRef(false);

  const fetchMe = async (jwt) => {
    try {
      setLoadingUser(true);
      setAuthError(null);
      api.setToken(jwt); // set token for API calls
      const data = await api.get("auth/me");

      setUser({
        id: data.user.id,
        username: data.user.username,
        email: data.user.email,
        role: data.user.role,
        isFirstLogin: data.user.isFirstLogin,
        permissions: data.user.permissions || {
          broadcasting: true,
          automation: true,
          contacts: true,
          templates: true,
        },
      });
    } catch (error) {
      console.error("Auth error:", error);
      
      // If session token failed, try remember token before giving up
      const rememberToken = localStorage.getItem("rememberToken");
      if (rememberToken) {
        await loginWithRememberToken();
      } else {
        setUser(null);
        setToken(null);
        api.removeToken();
        localStorage.removeItem("token");
        setAuthError("Session expired. Please log in again.");
      }
    } finally {
      setLoadingUser(false);
    }
  };

  const login = async (credentials) => {
    try {
      setAuthError(null);
      const response = await api.post("auth/login", credentials);

      if (response.success) {
        const { token: newToken, rememberToken, user: userData } = response;
        setToken(newToken);
        api.setToken(newToken);
        localStorage.setItem("token", newToken);

        // Store remember token if provided
        if (rememberToken) {
          localStorage.setItem("rememberToken", rememberToken);
        } else {
          localStorage.removeItem("rememberToken");
        }

        setUser({
          id: userData.id,
          username: userData.username,
          email: userData.email,
          role: userData.role,
          isFirstLogin: userData.isFirstLogin,
          permissions: userData.permissions || {
            broadcasting: true,
            automation: true,
            contacts: true,
            templates: true,
          },
        });
        return { success: true, user: userData };
      } else {
        const errorMessage =
          response.message || "Login failed. Please try again.";
        setAuthError(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      console.error("Login error:", error);

      // If backend returned a locked response, pass it through
      if (error.data?.locked) {
        const lockedMsg = error.data.message || "Too many failed attempts.";
        setAuthError(lockedMsg);
        return {
          success: false,
          locked: true,
          retryAfter: error.data.retryAfter || 30,
          error: lockedMsg,
        };
      }

      let errorMessage = "Invalid email or password. Please try again.";
      const msg = error?.message || "";
      if (!navigator.onLine || msg.includes("Network Error") || msg.includes("Failed to fetch")) {
        errorMessage = "You are offline. Check your internet connection.";
      } else if (msg.includes("timeout") || msg.includes("Timeout")) {
        errorMessage = "Server is taking longer than expected. Please try again.";
      } else if (msg.includes("429") || msg.includes("Too many")) {
        errorMessage = "Too many login attempts. Please try again after 30 seconds.";
      } else if (msg.includes("500") || msg.includes("502") || msg.includes("503") || msg.includes("Server error")) {
        errorMessage = "Something went wrong. Please try again later.";
      } else if (msg.includes("401") || msg.includes("Invalid")) {
        errorMessage = "Invalid email or password. Please try again.";
      }

      // Pass attemptsRemaining from backend if available
      const attemptsRemaining = error.data?.attemptsRemaining ?? null;

      setAuthError(errorMessage);
      return { success: false, error: errorMessage, attemptsRemaining };
    }
  };

  const loginWithRememberToken = async () => {
    try {
      setLoadingUser(true);
      const rememberToken = localStorage.getItem("rememberToken");
      if (!rememberToken) {
        return { success: false, error: "No remember token found" };
      }

      const response = await api.post("auth/login-remember", { rememberToken });

      if (response.success) {
        const { token: newToken, user: userData } = response;
        setToken(newToken);
        api.setToken(newToken);
        localStorage.setItem("token", newToken);
        setUser({
          id: userData.id,
          username: userData.username,
          email: userData.email,
          role: userData.role,
          isFirstLogin: userData.isFirstLogin,
          permissions: userData.permissions || {
            broadcasting: true,
            automation: true,
            contacts: true,
            templates: true,
          },
        });
        return { success: true };
      } else {
        // Clear invalid remember token
        localStorage.removeItem("rememberToken");
        return { success: false, error: response.message };
      }
    } catch (error) {
      console.error("Auto-login error:", error);
      localStorage.removeItem("rememberToken");
      return { success: false, error: "Auto-login failed" };
    } finally {
      setLoadingUser(false);
    }
  };

  const register = async (userData) => {
    try {
      setAuthError(null);
      const response = await api.post("auth/register", userData);

      if (response.success) {
        // Registration successful, but do not auto-login
        return { success: true, reregistered: !!response.reregistered };
      } else {
        const errorMessage =
          response.message || "Registration failed. Please try again.";
        setAuthError(errorMessage);
        return { success: false, error: error.message };
      }
    } catch (error) {
      console.error("Registration error:", error);

      let errorMessage = "Registration failed. Please try again.";
      const msg = error?.message || "";
      if (!navigator.onLine || msg.includes("Network Error") || msg.includes("Failed to fetch")) {
        errorMessage = "You are offline. Check your internet connection.";
      } else if (msg.includes("timeout") || msg.includes("Timeout")) {
        errorMessage = "Server is taking longer than expected. Please try again.";
      } else if (msg.includes("409") || msg.includes("already exists") || msg.includes("Email already")) {
        errorMessage = "Email already registered. Try signing in instead.";
      } else if (msg.includes("500") || msg.includes("502") || msg.includes("503") || msg.includes("Server error")) {
        errorMessage = "Something went wrong. Please try again later.";
      } else if (msg.includes("400") || msg.includes("Validation")) {
        errorMessage = "Please check your information and try again.";
      }

      setAuthError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    api.removeToken();
    localStorage.removeItem("token");
    localStorage.removeItem("rememberToken");
    setAuthError(null);
  };

  const clearAuthError = () => {
    setAuthError(null);
  };

  // Programmatically establish an auth session (used after verify-email / reset-password)
  const setAuthSession = (newToken, userData) => {
    setAuthError(null);
    setToken(newToken);
    api.setToken(newToken);
    localStorage.setItem("token", newToken);
    setUser({
      id: userData.id,
      username: userData.username,
      email: userData.email,
      role: userData.role,
      isFirstLogin: userData.isFirstLogin,
      permissions: userData.permissions || {
        broadcasting: true,
        automation: true,
        contacts: true,
        templates: true,
      },
    });
  };

  useEffect(() => {
    // Guard against React StrictMode double-invocation
    if (hasMounted.current) return;
    hasMounted.current = true;

    const storedToken = localStorage.getItem("token");
    if (storedToken) {
      setToken(storedToken);
      fetchMe(storedToken);
    } else {
      // Try auto-login with remember token if no session token
      const rememberToken = localStorage.getItem("rememberToken");
      if (rememberToken) {
        loginWithRememberToken();
      }
    }
  }, []);

  const updateUser = (updatedUserData) => {
    setUser((prevUser) => ({ ...prevUser, ...updatedUserData }));
  };

  const value = {
    user,
    token,
    loadingUser,
    authError,
    login,
    register,
    logout,
    clearAuthError,
    updateUser,
    setAuthSession,
    isAuthenticated: !!user && !!token,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
